/**
 * LOVE Bags 2026 — PDP JavaScript
 * Version: v8.7.3 (2026-09-09 — rebuildThumbs skips variant photo slot so variant clicks don't produce a 9th orphan thumb)
 *
 * Diagnostic-first: every early exit and every init step logs to console
 * under the [LB PDP] prefix so a silent failure never happens again.
 *
 * Handles:
 *   - Print swatch click  → variant commit (URL + price + image + name + CTA)
 *   - Print swatch hover  → temporary image preview (reverts on mouseleave)
 *   - Print swatch focus  → same as hover (keyboard a11y)
 *   - Thumbnail click     → main image swap (via Gallery.goTo, event-delegated)
 *   - Prev/next arrows    → cycle hero image (v8.7)
 *   - Keyboard arrows     → cycle hero when focus is in the images column (v8.7)
 *   - Variant commit      → Gallery rebuilds thumbs from that variant's images (v8.7)
 *   - Cross-sell refresh  → via Sections API on variant change
 *   - "What fits" trigger → scrolls to info blocks
 *
 * Vanilla ES2017+, no jQuery. Works with Flex 6.0.
 */

(function () {
  'use strict';

  // Immediate breadcrumb: proves the script parsed and started executing.
  // If this line is missing from the console, the script tag isn't loading
  // (check Network tab for pdp-2026.js) or the file is being cached.
  console.info('[LB PDP v8.5] script executing');

  // ============================================
  // STATE
  // ============================================

  const productContainer = document.querySelector('[data-lb-product-main]');
  if (!productContainer) {
    console.warn(
      '[LB PDP] STOP: could not find [data-lb-product-main] in the DOM. ' +
      'Either the product-main-2026 section is not rendering, or the ' +
      'data-lb-product-main attribute has been stripped from the <section> tag. ' +
      'View page source and search for "data-lb-product-main" to check.'
    );
    return;
  }
  console.info('[LB PDP] container found');

  const productHandle = productContainer.dataset.productHandle;
  const productId     = productContainer.dataset.productId;
  const productUrl    = productContainer.dataset.productUrl;

  if (!productUrl) {
    console.warn(
      '[LB PDP] STOP: data-product-url is empty on the container. ' +
      'The section is rendering but Liquid did not emit the product URL. ' +
      'Cannot fetch variant JSON without it.'
    );
    return;
  }
  console.info('[LB PDP] product URL:', productUrl);

  let currentVariant = null;
  let productData    = null;

  // ============================================
  // GALLERY MODULE (v8.7)
  // ============================================
  // Manages the composed image gallery:
  //   Slot 0     — current variant's featured_image (the "print you're looking at")
  //                Auto-updates when a print swatch is hovered or clicked, keeping
  //                the hero and the gallery counter in lockstep.
  //   Slots 1..N — variant.custom.lifestyle_images (post-launch, variant-scoped)
  //                OR product.custom.pdp_gallery_images (launch fallback, shared)
  //   Slots N+1..M — product.custom.pdp_detail_images (always shared)
  //
  // State is a flat array of {src, zoom, thumb, alt}. On variant commit,
  // the array is recomposed and thumbs are rebuilt so hover/select of a
  // print swatch always lands on that variant's own product photo (slot 0).
  // Arrows and thumbs then advance forward through lifestyle + detail.
  const Gallery = (function () {
    let data                = null;
    let currentList         = [];
    let currentHasVariantHero = false;
    let currentIndex        = 0;
    let currentVariantId    = null;
    let heroImgEl           = null;

    function loadData() {
      const el = document.querySelector('[data-lb-gallery-data]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); }
      catch (e) { console.error('[LB Gallery] JSON parse failed', e); return null; }
    }

    function composeList(variantId) {
      if (!data) return { list: [], hasVariantHero: false };
      const list = [];
      let hasVariantHero = false;

      // Slot 0: current variant's own product photo — auto-updates on variant swap.
      // Included so the hover/select swatch behavior stays in lockstep with the
      // gallery counter, and so arrows pick up naturally from the variant view.
      // NOTE (v8.7.3): slot 0 does NOT render its own thumbnail — matches the
      // Liquid initial render's 8-thumb grid. hasVariantHero tells rebuildThumbs
      // to start iterating at index 1.
      if (productData) {
        const v = productData.variants.find(x => x.id == variantId);
        if (v && v.featured_image && v.featured_image.src) {
          list.push({
            src:   v.featured_image.src,
            zoom:  v.featured_image.src,
            thumb: v.featured_image.src,
            alt:   v.featured_image.alt || productData.title || ''
          });
          hasVariantHero = true;
        }
      }

      // Slots 1..N: variant lifestyle (post-launch) OR product lifestyle (launch fallback)
      const vMeta = (data.variants && (data.variants[String(variantId)] || data.variants[variantId])) || {};
      const vLifestyle = Array.isArray(vMeta.lifestyle) ? vMeta.lifestyle : [];
      const lifestyle  = vLifestyle.length > 0 ? vLifestyle : (data.product_lifestyle || []);
      lifestyle.forEach(img => list.push(img));

      // Slots N+1..M: product detail (always same across variants)
      (data.product_detail || []).forEach(img => list.push(img));

      return { list, hasVariantHero };
    }

    function updateHero() {
      if (!heroImgEl) heroImgEl = document.querySelector('[data-lb-hero-image]');
      if (!heroImgEl || !currentList[currentIndex]) return;
      const item = currentList[currentIndex];
      heroImgEl.src = item.src;
      heroImgEl.alt = item.alt || '';
      if (item.zoom) heroImgEl.setAttribute('data-lb-hero-zoom-src', item.zoom);
      // Clear any hover-preview breadcrumb — the hero is now authoritative for this variant
      delete heroImgEl.dataset.lbPreviewOriginal;
    }

    function updateCounter() {
      const cur = document.querySelector('[data-lb-gallery-current]');
      const tot = document.querySelector('[data-lb-gallery-total]');
      if (cur) cur.textContent = String(currentIndex + 1);
      if (tot) tot.textContent = String(currentList.length);
    }

    function updateActiveThumb() {
      document.querySelectorAll('[data-lb-thumb]').forEach(btn => {
        const idx      = parseInt(btn.dataset.galleryIndex, 10);
        const isActive = idx === currentIndex;
        btn.classList.toggle('lb-product-main__thumb--active', isActive);
        btn.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    function rebuildThumbs() {
      const container = document.querySelector('[data-lb-thumbs-container]');
      if (!container) return;
      const total = currentList.length;
      // v8.7.3: If slot 0 is the variant photo (auto-updates as user picks
      // prints), skip rendering a thumbnail for it — the Liquid initial
      // render skips it too. Keeps the thumb grid at exactly 8 slots.
      const startIdx = currentHasVariantHero ? 1 : 0;
      let html = '';
      for (let i = startIdx; i < currentList.length; i++) {
        const item = currentList[i];
        const active = i === currentIndex;
        html += `<button type="button" class="lb-product-main__thumb${active ? ' lb-product-main__thumb--active' : ''}"`;
        html += ` data-lb-thumb data-gallery-index="${i}"`;
        html += ` data-full-url="${item.src}" data-zoom-url="${item.zoom || ''}"`;
        html += ` aria-label="Show image ${i + 1} of ${total}"${active ? ' aria-current="true"' : ''}>`;
        html += `<img src="${item.thumb || item.src}" alt="" loading="lazy" width="100" height="100">`;
        html += `</button>`;
      }
      container.innerHTML = html;
    }

    function toggleNavVisibility() {
      const hero = document.querySelector('[data-lb-product-hero]');
      if (!hero) return;
      const showNav = currentList.length > 1;
      hero.querySelectorAll('[data-lb-gallery-prev], [data-lb-gallery-next], [data-lb-gallery-counter]').forEach(el => {
        el.style.display = showNav ? '' : 'none';
      });
      const thumbs = document.querySelector('[data-lb-thumbs-container]');
      if (thumbs) thumbs.style.display = showNav ? '' : 'none';
    }

    function goTo(index) {
      if (!currentList.length) return;
      if (index < 0)                  index = currentList.length - 1;
      if (index >= currentList.length) index = 0;
      currentIndex = index;
      updateHero();
      updateActiveThumb();
      updateCounter();
    }

    function next() { goTo(currentIndex + 1); }
    function prev() { goTo(currentIndex - 1); }

    function syncToVariant(variantId) {
      if (!data) return;
      currentVariantId = variantId;
      const composed = composeList(variantId);
      currentList           = composed.list;
      currentHasVariantHero = composed.hasVariantHero;
      currentIndex          = 0;
      rebuildThumbs();
      updateHero();
      updateCounter();
      toggleNavVisibility();
    }

    function currentHeroSrc() {
      return currentList[currentIndex] ? currentList[currentIndex].src : null;
    }

    function bindControls() {
      const prevBtn = document.querySelector('[data-lb-gallery-prev]');
      const nextBtn = document.querySelector('[data-lb-gallery-next]');
      if (prevBtn) prevBtn.addEventListener('click', prev);
      if (nextBtn) nextBtn.addEventListener('click', next);

      // Keyboard: arrow keys when focus is anywhere inside the images column
      const imagesCol = document.querySelector('.lb-product-main__images');
      if (imagesCol) {
        imagesCol.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft')  { e.preventDefault(); prev(); }
          if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
        });
      }

      // Delegate thumb clicks to the container — survives rebuildThumbs() DOM swaps
      const thumbsContainer = document.querySelector('[data-lb-thumbs-container]');
      if (thumbsContainer) {
        thumbsContainer.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-lb-thumb]');
          if (!btn) return;
          const idx = parseInt(btn.dataset.galleryIndex, 10);
          if (!isNaN(idx)) goTo(idx);
        });
      }
    }

    function init(initialVariantId) {
      data = loadData();
      if (!data) {
        console.warn('[LB Gallery] no [data-lb-gallery-data] found — reactive swap disabled');
        return;
      }
      currentVariantId = initialVariantId;
      const composed = composeList(initialVariantId);
      currentList           = composed.list;
      currentHasVariantHero = composed.hasVariantHero;
      currentIndex          = 0;
      heroImgEl        = document.querySelector('[data-lb-hero-image]');
      bindControls();
      updateCounter();
      console.info('[LB Gallery] init · variant', initialVariantId, '· images =', currentList.length, '· hasVariantHero =', currentHasVariantHero);
    }

    return { init, syncToVariant, goTo, next, prev, currentHeroSrc };
  })();

  // ============================================
  // INITIALIZATION
  // ============================================

  async function init() {
    console.info('[LB PDP] init running');
    try {
      productData = loadProductData();
      console.info('[LB PDP] product data loaded · variants =', productData.variants.length);

      const initialVariantId = getVariantIdFromURL() || productData.variants[0].id;
      currentVariant = productData.variants.find(v => v.id == initialVariantId) || productData.variants[0];
      console.info('[LB PDP] initial variant:', currentVariant.id, currentVariant.title);

      const bindCount = bindSwatchInteractions();
      Gallery.init(currentVariant.id);
      bindWhatFitsInsideModal();
      console.info('[LB PDP] bound', bindCount, 'swatch(es) for click + hover + focus');

      // Final health line
      const variantsWithImages = productData.variants.filter(v => v.featured_image).length;
      const variantsTotal      = productData.variants.length;
      const missingImages      = variantsTotal - variantsWithImages;
      console.info(
        `[LB PDP] ready · product="${productData.title}" · ` +
        `swatches=${bindCount} · variants=${variantsTotal} · ` +
        `variants_with_images=${variantsWithImages}` +
        (missingImages > 0
          ? ` · WARN: ${missingImages} variant(s) missing featured_image — image swap won't fire for those`
          : '')
      );
    } catch (err) {
      console.error('[LB PDP] Initialization failed:', err);
    }
  }

  // ============================================
  // PRODUCT DATA LOADING
  // ============================================

  /**
   * Primary path: read from inline <script data-lb-product-json> emitted by
   * the section. This is synchronous, works on ALL Shopify URL contexts
   * (production, preview, password-protected), and avoids a network hop.
   *
   * Fallback path: if the inline element is missing (e.g. someone injected
   * the JS on a page without the section), fall back to the network fetch.
   * The fetch may 404 on shopifypreview.com — that's why inline is preferred.
   */
  function loadProductData() {
    const inlineEl = document.querySelector('[data-lb-product-json]');
    if (inlineEl && inlineEl.textContent.trim()) {
      try {
        const data = JSON.parse(inlineEl.textContent);
        console.info('[LB PDP] read variant data from inline JSON');
        return data;
      } catch (err) {
        console.warn('[LB PDP] inline JSON parse failed, falling back to network:', err);
      }
    }
    // Async fallback — only used if inline is missing or malformed.
    // Wrapped in a Promise so init() can `await` uniformly if needed.
    throw new Error(
      'No inline product JSON found. The section may not be rendering the ' +
      '<script data-lb-product-json> block, or Liquid output was suppressed.'
    );
  }

  // ============================================
  // URL PARAMETER (replaceState — no history push)
  // ============================================

  function getVariantIdFromURL() {
    return new URLSearchParams(window.location.search).get('variant');
  }

  function updateURLVariant(variantId) {
    const params = new URLSearchParams(window.location.search);
    params.set('variant', variantId);
    const newURL = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState({ variantId }, '', newURL);
  }

  // ============================================
  // SWATCH INTERACTIONS: click, hover, focus
  // ============================================

  function bindSwatchInteractions() {
    const swatches = document.querySelectorAll('[data-lb-swatch]');
    swatches.forEach(swatch => {
      swatch.addEventListener('click',      handleSwatchClick);
      swatch.addEventListener('mouseenter', handleSwatchPreview);
      swatch.addEventListener('focusin',    handleSwatchPreview);
      swatch.addEventListener('mouseleave', handleSwatchPreviewEnd);
      swatch.addEventListener('focusout',   handleSwatchPreviewEnd);
    });
    return swatches.length;
  }

  function handleSwatchClick(event) {
    event.preventDefault();
    const swatch    = event.currentTarget;
    const variantId = parseInt(swatch.dataset.variantId, 10);
    const variant   = productData && productData.variants.find(v => v.id === variantId);
    if (!variant) {
      console.warn('[LB PDP] click: no variant found for id', variantId);
      return;
    }

    updateSwatchSelection(swatch);
    updateVariant(variant);
    updateURLVariant(variant.id);
    refreshCrossSell(variant);
  }

  function handleSwatchPreview(event) {
    const swatch    = event.currentTarget;
    const variantId = parseInt(swatch.dataset.variantId, 10);
    const variant   = productData && productData.variants.find(v => v.id === variantId);
    if (!variant || !variant.featured_image) return;

    const heroImg = document.querySelector('[data-lb-hero-image]');
    if (!heroImg) return;

    // Stash the current src the first time we preview, so mouseleave can
    // restore the correct baseline. Cleared on preview end.
    if (!heroImg.dataset.lbPreviewOriginal) {
      heroImg.dataset.lbPreviewOriginal = heroImg.src;
    }
    heroImg.src = variant.featured_image.src;
  }

  function handleSwatchPreviewEnd() {
    const heroImg = document.querySelector('[data-lb-hero-image]');
    if (!heroImg) return;

    // v8.7: Prefer restoring to the Gallery's current image — that's the
    // source of truth. Falls back to the stashed original if Gallery hasn't
    // initialized yet, or to the current variant's featured image.
    const galleryHero = Gallery.currentHeroSrc();
    if (galleryHero) {
      heroImg.src = galleryHero;
    } else if (currentVariant && currentVariant.featured_image) {
      heroImg.src = currentVariant.featured_image.src;
    } else if (heroImg.dataset.lbPreviewOriginal) {
      heroImg.src = heroImg.dataset.lbPreviewOriginal;
    }
    delete heroImg.dataset.lbPreviewOriginal;
  }

  function updateSwatchSelection(selectedSwatch) {
    document.querySelectorAll('[data-lb-swatch]').forEach(s => {
      s.classList.remove('lb-print-swatch--selected');
      s.setAttribute('aria-pressed', 'false');
    });
    selectedSwatch.classList.add('lb-print-swatch--selected');
    selectedSwatch.setAttribute('aria-pressed', 'true');
  }

  // ============================================
  // VARIANT COMMIT (price, image, cart button, print name)
  // ============================================

  function updateVariant(variant) {
    currentVariant = variant;
    updatePrintName(variant);
    updatePrice(variant);
    updateFeaturedImage(variant);
    updateAddToCart(variant);
    updateHiddenVariantInput(variant);
  }

  function updatePrintName(variant) {
    const el = document.querySelector('[data-lb-print-current]');
    if (el) el.textContent = variant.public_title || variant.title || 'Print';
  }

  function updatePrice(variant) {
    const priceCurrentEl = document.querySelector('[data-lb-price-current]');
    const priceCompareEl = document.querySelector('[data-lb-price-compare]');
    const btnPriceEl     = document.querySelector('[data-lb-btn-price]');
    const installmentEl  = document.querySelector('[data-lb-installment-amount]');

    if (priceCurrentEl) priceCurrentEl.textContent = formatMoney(variant.price);
    if (btnPriceEl)     btnPriceEl.textContent     = formatMoney(variant.price);
    if (installmentEl)  installmentEl.textContent  = formatMoney(Math.round(variant.price / 4));

    if (priceCompareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceCompareEl.textContent  = formatMoney(variant.compare_at_price);
        priceCompareEl.style.display = '';
      } else {
        priceCompareEl.style.display = 'none';
      }
    }
  }

  function updateFeaturedImage(variant) {
    // v8.7.2: Gallery module owns the hero. It recomposes the gallery for the
    // newly-selected variant (variant.featured_image at slot 0, then variant.lifestyle
    // OR product.pdp_gallery_images, then product.pdp_detail_images), rebuilds thumbs,
    // resets to slot 0, and updates the counter — all in one call.
    Gallery.syncToVariant(variant.id);
  }

  // [v8.7] DEPRECATED — kept for reference. Gallery.updateActiveThumb() replaces this.
  function updateActiveThumbnail(variant) {
    if (!variant.featured_media) return;
    document.querySelectorAll('[data-lb-thumb]').forEach(thumb => {
      const mediaId  = parseInt(thumb.dataset.mediaId, 10);
      const isActive = mediaId === variant.featured_media.id;
      thumb.classList.toggle('lb-product-main__thumb--active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function updateAddToCart(variant) {
    const btn     = document.querySelector('[data-lb-add-to-cart]');
    const btnText = document.querySelector('[data-lb-add-text]');
    if (!btn || !btnText) return;

    if (variant.available) {
      btn.disabled = false;
      btnText.innerHTML = `Add to cart — <span data-lb-btn-price>${formatMoney(variant.price)}</span>`;
    } else {
      btn.disabled = true;
      btnText.textContent = 'Sold out';
    }
  }

  function updateHiddenVariantInput(variant) {
    const input = document.querySelector('[data-lb-variant-id]');
    if (input) input.value = variant.id;
  }

  // ============================================
  // THUMBNAIL CLICKS  [v8.7 DEPRECATED — Gallery module handles this via delegation]
  // ============================================
  // Left in place for reference / safe rollback. Not called from init() anymore.

  function bindThumbnailClicks() {
    document.querySelectorAll('[data-lb-thumb]').forEach(thumb => {
      thumb.addEventListener('click', handleThumbnailClick);
    });
  }

  function handleThumbnailClick(event) {
    const thumb   = event.currentTarget;
    const fullUrl = thumb.dataset.fullUrl;
    const heroImg = document.querySelector('[data-lb-hero-image]');

    if (heroImg && fullUrl) heroImg.src = fullUrl;

    document.querySelectorAll('[data-lb-thumb]').forEach(t => {
      t.classList.remove('lb-product-main__thumb--active');
      t.setAttribute('aria-current', 'false');
    });
    thumb.classList.add('lb-product-main__thumb--active');
    thumb.setAttribute('aria-current', 'true');
  }

  // ============================================
  // CROSS-SELL REFRESH (Sections API)
  // ============================================

  async function refreshCrossSell(variant) {
    const crossSellSection = document.querySelector('[data-lb-shop-the-print]');
    if (!crossSellSection) return;

    // Get the ACTUAL Shopify section ID from the parent wrapper.
    // JSON-template sections use format `template--<content_id>__<key>`,
    // not the short section key. The wrapper carries the real ID.
    const shopifyWrapper = crossSellSection.closest('[id^="shopify-section-"]');
    if (!shopifyWrapper) {
      console.warn('[LB PDP] Cross-sell refresh: no Shopify section wrapper');
      return;
    }
    const sectionId = shopifyWrapper.id.replace('shopify-section-', '');

    try {
      // Build fetch URL, preserving preview_key on Shopify preview URLs.
      const fetchUrl = new URL(productUrl, window.location.origin);
      fetchUrl.searchParams.set('variant', variant.id);
      fetchUrl.searchParams.set('section_id', sectionId);
      const currentPreviewKey = new URL(window.location.href).searchParams.get('preview_key');
      if (currentPreviewKey) fetchUrl.searchParams.set('preview_key', currentPreviewKey);

      console.info('[LB PDP] Cross-sell refresh → ' + fetchUrl.pathname + fetchUrl.search);
      const response = await fetch(fetchUrl.toString(), { credentials: 'same-origin' });

      if (!response.ok) {
        console.warn('[LB PDP] Cross-sell refresh: HTTP', response.status, response.statusText);
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('[data-lb-shop-the-print]');

      if (newSection) {
        crossSellSection.replaceWith(newSection);
        console.info('[LB PDP] Cross-sell refresh ✓');
      } else {
        crossSellSection.style.display = 'none';
        console.info('[LB PDP] Cross-sell refresh: no matches, hiding section');
      }
    } catch (err) {
      console.warn('[LB PDP] Cross-sell refresh failed silently:', err);
    }
  }

  // ============================================
  // "WHAT FITS INSIDE" MODAL (scroll for now)
  // ============================================

  function bindWhatFitsInsideModal() {
    const trigger = document.querySelector('[data-lb-open-modal="what-fits-inside"]');
    if (!trigger) return;
    trigger.addEventListener('click', () => {
      const infoBlocks = document.querySelector('[data-lb-info-blocks]');
      if (infoBlocks) infoBlocks.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ============================================
  // MONEY FORMATTING
  // ============================================

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '$0.00';
    const dollars = (cents / 100).toFixed(2);
    const parts = dollars.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  // ============================================
  // BOOTSTRAP
  // ============================================

  // ============================================
  // HERO IMAGE HOVER ZOOM (v8.5)
  // Follows the cursor for a magnifier-style effect. Adds `is-zooming`
  // class on mouseenter, updates transform-origin on mousemove, removes
  // on mouseleave. Bails on touch devices via CSS @media (hover: none).
  // ============================================
  function initHeroZoom() {
    const hero = document.querySelector('[data-lb-hero-zoom]');
    if (!hero) return;
    const heroImg = hero.querySelector('[data-lb-hero-image]');
    if (!heroImg) return;

    // Detect touch — skip zoom setup entirely (CSS already disables the transform)
    if (window.matchMedia && window.matchMedia('(hover: none)').matches) {
      return;
    }

    hero.addEventListener('mouseenter', function () {
      hero.classList.add('is-zooming');
    });

    hero.addEventListener('mouseleave', function () {
      hero.classList.remove('is-zooming');
      heroImg.style.transformOrigin = 'center center';
    });

    hero.addEventListener('mousemove', function (e) {
      const rect = hero.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      // Clamp to prevent transform-origin going outside the image bounds
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));
      heroImg.style.transformOrigin = clampedX + '% ' + clampedY + '%';
    });

    console.info('[LB PDP v8.5] hero zoom wired');
  }

  // ============================================
  // RELATED PRODUCTS — Shopify Product Recommendations API (v8.5)
  // Server renders a fallback collection or skeleton on initial page load;
  // this JS attempts to fetch real personalized recommendations and swap
  // them in. If the API 404s or returns nothing (common on preview URLs),
  // the server-rendered fallback stays visible.
  // ============================================
  async function fetchRecommendations() {
    const container = document.querySelector('[data-lb-pdp-related]');
    if (!container) return;

    // Only fetch for shopify_auto source
    const source = container.dataset.recsSource;
    if (source !== 'shopify_auto') return;

    // Skip if server already resolved via the API fetch pass
    if (container.dataset.apiFetch === 'true') return;

    const productId = container.dataset.productId;
    const cardCount = container.dataset.cardCount || 4;
    // Use routes.product_recommendations_url from Liquid for portability across
    // stores (Markets, language subpaths, etc.)
    const baseUrl = container.dataset.recsUrl || '/recommendations/products';

    // Discover the Shopify section ID from the DOM
    const shopifyWrapper = container.closest('[id^="shopify-section-"]');
    if (!shopifyWrapper) {
      console.warn('[LB Related] no Shopify section wrapper');
      return;
    }
    const sectionId = shopifyWrapper.id.replace('shopify-section-', '');

    // Build fetch URL — preserve preview_key
    const fetchUrl = new URL(baseUrl, window.location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);
    fetchUrl.searchParams.set('product_id', productId);
    fetchUrl.searchParams.set('limit', cardCount);
    const currentPreviewKey = new URL(window.location.href).searchParams.get('preview_key');
    if (currentPreviewKey) fetchUrl.searchParams.set('preview_key', currentPreviewKey);

    console.info('[LB Related] fetch →', fetchUrl.pathname + fetchUrl.search);

    try {
      const response = await fetch(fetchUrl.toString(), { credentials: 'same-origin' });

      if (!response.ok) {
        // On preview URLs this endpoint frequently 404s. That's fine — we
        // already have server-rendered fallback content (curated collection
        // or skeleton). Log and leave the DOM as-is.
        console.info('[LB Related] API returned', response.status, '— keeping server-rendered fallback');
        // If we're stuck on a skeleton with no fallback, hide it
        const loadingSkeleton = container.querySelector('[data-lb-related-loading]');
        if (loadingSkeleton && !container.querySelector('[data-lb-related-grid]')) {
          container.style.display = 'none';
        }
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('[data-lb-pdp-related]');

      // Only replace if the API returned an actual populated grid
      // (recommendations.performed = true AND products count > 0)
      const newGrid = newSection && newSection.querySelector('[data-lb-related-grid]');
      if (newSection && newGrid && newGrid.children.length > 0) {
        container.replaceWith(newSection);
        console.info('[LB Related] populated with API results ✓');
      } else {
        // API responded but no recommendations available for this product.
        // Keep whatever the server rendered (fallback or skeleton).
        console.info('[LB Related] API returned no products — keeping server-rendered content');
        const loadingSkeleton = container.querySelector('[data-lb-related-loading]');
        if (loadingSkeleton && !container.querySelector('[data-lb-related-grid]')) {
          container.style.display = 'none';
        }
      }
    } catch (err) {
      console.info('[LB Related] fetch error, keeping fallback:', err.message);
    }
  }

  if (document.readyState === 'loading') {
    console.info('[LB PDP] deferring init to DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', function () {
      init();
      initHeroZoom();
      fetchRecommendations();
    });
  } else {
    init();
    initHeroZoom();
    fetchRecommendations();
  }

})();
